import React, { useState, useEffect, useCallback } from 'react';

const API_URL = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

const ROLLEN = ['Lagerleiter', 'Einkäufer', 'Verkäufer', 'Produktionsleiter', 'Buchhaltung', 'Geschäftsführer', 'Admin'];

interface Schnellanfrage {
  id: number;
  rolle: string;
  text: string;
  position: number;
  aktiv: boolean;
}

interface DirtyRow {
  text?: string;
  aktiv?: boolean;
}

interface SchnellanfragenConfigProps {
  authToken: string;
}

const SchnellanfragenConfig: React.FC<SchnellanfragenConfigProps> = ({ authToken }) => {
  const [selectedRolle, setSelectedRolle] = useState<string>(ROLLEN[0]);
  const [alle, setAlle] = useState<Schnellanfrage[]>([]);
  const [dirty, setDirty] = useState<Record<number, DirtyRow>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newText, setNewText] = useState('');

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${authToken}`,
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_URL}/api/schnellanfragen`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data: Schnellanfrage[] = await r.json();
      setAlle(data);
      setDirty({});
    } catch (e: any) {
      setError(e.message ?? 'Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authToken]);

  useEffect(() => { load(); }, [load]);

  const currentRows = alle
    .filter(a => a.rolle === selectedRolle)
    .sort((a, b) => a.position - b.position);

  const markDirty = (id: number, patch: DirtyRow) => {
    setDirty(prev => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const saveRow = async (row: Schnellanfrage) => {
    const changes = dirty[row.id];
    if (!changes) return;
    try {
      const r = await fetch(`${API_URL}/api/schnellanfragen/${row.id}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify(changes),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setDirty(prev => { const n = { ...prev }; delete n[row.id]; return n; });
      await load();
    } catch (e: any) {
      setError(e.message ?? 'Fehler beim Speichern');
    }
  };

  const deleteRow = async (id: number) => {
    if (!window.confirm('Eintrag wirklich löschen?')) return;
    try {
      const r = await fetch(`${API_URL}/api/schnellanfragen/${id}`, {
        method: 'DELETE',
        headers,
      });
      if (!r.ok && r.status !== 204) throw new Error(`HTTP ${r.status}`);
      await load();
    } catch (e: any) {
      setError(e.message ?? 'Fehler beim Löschen');
    }
  };

  const addRow = async () => {
    if (!newText.trim()) return;
    const maxPos = currentRows.length > 0 ? Math.max(...currentRows.map(r => r.position)) + 1 : 0;
    try {
      const r = await fetch(`${API_URL}/api/schnellanfragen`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ rolle: selectedRolle, text: newText.trim(), position: maxPos, aktiv: true }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setNewText('');
      await load();
    } catch (e: any) {
      setError(e.message ?? 'Fehler beim Anlegen');
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm(`Alle Einträge für "${selectedRolle}" werden gelöscht und durch Defaults ersetzt. Fortfahren?`)) return;
    try {
      const r = await fetch(`${API_URL}/api/schnellanfragen/reset`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ rolle: selectedRolle }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await load();
    } catch (e: any) {
      setError(e.message ?? 'Fehler beim Reset');
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '900px' }}>
      <h2 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: 600 }}>Schnellanfragen verwalten</h2>

      {error && (
        <div style={{ background: 'var(--red-50, var(--bone-2))', border: '1px solid var(--red-200, var(--red))', borderRadius: 0, padding: '10px 14px', marginBottom: '12px', color: 'var(--red-700, var(--red))' }}>
          {error}
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <label style={{ fontSize: '14px', fontWeight: 500 }}>Rolle:</label>
        <select
          value={selectedRolle}
          onChange={e => { setSelectedRolle(e.target.value); setDirty({}); }}
          style={{ padding: '6px 10px', borderRadius: 0, border: '1px solid var(--bone-3)', background: 'var(--bone)', fontSize: '14px', cursor: 'pointer' }}
        >
          {ROLLEN.map(r => <option key={r} value={r}>{r}</option>)}
        </select>
        <button
          onClick={resetDefaults}
          style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 0, border: '1px solid var(--bone-3)', background: 'var(--bone-2)', fontSize: '13px', cursor: 'pointer', color: 'var(--ink-2)' }}
        >
          Defaults wiederherstellen
        </button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--ink-2)', fontSize: '14px' }}>Lädt...</p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid var(--bone-3)' }}>
              <th style={{ textAlign: 'left', padding: '8px 10px', width: '60px', color: 'var(--ink-2)', fontWeight: 500 }}>Pos.</th>
              <th style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--ink-2)', fontWeight: 500 }}>Text</th>
              <th style={{ textAlign: 'center', padding: '8px 10px', width: '80px', color: 'var(--ink-2)', fontWeight: 500 }}>Aktiv</th>
              <th style={{ padding: '8px 10px', width: '140px' }}></th>
            </tr>
          </thead>
          <tbody>
            {currentRows.map(row => {
              const d = dirty[row.id] ?? {};
              const currentText = d.text !== undefined ? d.text : row.text;
              const currentAktiv = d.aktiv !== undefined ? d.aktiv : row.aktiv;
              const isDirty = !!dirty[row.id];
              return (
                <tr key={row.id} style={{ borderBottom: '1px solid var(--bone-3)', background: isDirty ? 'rgba(61,124,46,0.04)' : undefined }}>
                  <td style={{ padding: '8px 10px', color: 'var(--ink-2)' }}>{row.position}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <input
                      value={currentText}
                      onChange={e => markDirty(row.id, { text: e.target.value })}
                      style={{ width: '100%', padding: '5px 8px', borderRadius: 0, border: '1px solid var(--bone-3)', fontSize: '14px', background: 'var(--bone)' }}
                    />
                  </td>
                  <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={currentAktiv}
                      onChange={e => markDirty(row.id, { aktiv: e.target.checked })}
                      style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                    />
                  </td>
                  <td style={{ padding: '8px 10px', display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                    {isDirty && (
                      <button
                        onClick={() => saveRow(row)}
                        style={{ padding: '4px 10px', borderRadius: 0, border: 'none', background: 'var(--green-500, var(--green-500))', color: 'var(--bone)', fontSize: '12px', cursor: 'pointer' }}
                      >
                        Speichern
                      </button>
                    )}
                    <button
                      onClick={() => deleteRow(row.id)}
                      style={{ padding: '4px 10px', borderRadius: 0, border: '1px solid var(--bone-3)', background: 'var(--bone-2)', color: 'var(--ink-2)', fontSize: '12px', cursor: 'pointer' }}
                    >
                      Löschen
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div style={{ marginTop: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
        <input
          value={newText}
          onChange={e => setNewText(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') addRow(); }}
          placeholder="Neue Anfrage eingeben…"
          style={{ flex: 1, padding: '7px 10px', borderRadius: 0, border: '1px solid var(--bone-3)', fontSize: '14px', background: 'var(--bone)' }}
        />
        <button
          onClick={addRow}
          style={{ padding: '7px 16px', borderRadius: 0, border: 'none', background: 'var(--green-500, var(--green-500))', color: 'var(--bone)', fontSize: '14px', cursor: 'pointer' }}
        >
          + Hinzufügen
        </button>
      </div>
    </div>
  );
};

export default SchnellanfragenConfig;
